import { WifiOff } from 'lucide-react';
import { useMemo } from 'react';
import { useSignatureOutbox } from '@/hooks/use-signature-outbox';
import { Button } from '@/app/components/ui/button';
import type { SyncResult } from '@/lib/offline/sync-signatures';

export interface OfflineSignatureIndicatorProps {
  onSync?: (result: SyncResult) => void;
}

export function OfflineSignatureIndicator({ onSync }: OfflineSignatureIndicatorProps) {
  const { isOffline, queuedCount, pendingCount, failedCount, conflictCount, isSyncing, syncNow, retryFailed, items } = useSignatureOutbox({ onSync });

  const failedItems = useMemo(() => items.filter((item) => item.status === 'failed'), [items]);
  const conflictItems = useMemo(() => items.filter((item) => item.status === 'conflict'), [items]);

  if (!isOffline && queuedCount === 0 && !isSyncing) {
    return null;
  }

  const plural = (n: number) => (n === 1 ? 'signature' : 'signatures');
  const conflictFallback = 'This signature could not be recorded because the JSA is no longer accepting signatures. Your capture is preserved; contact your supervisor.';

  return (
    <div role="status" aria-live="polite" className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 shadow-sm">
      {isOffline && (
        <div className="flex items-center gap-2">
          <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{pendingCount > 0 ? `Offline — ${pendingCount} ${plural(pendingCount)} queued, will sync when back online` : 'Offline'}</span>
        </div>
      )}
      {!isOffline && isSyncing && (
        <div>Syncing {pendingCount || queuedCount} {plural(pendingCount || queuedCount)}…</div>
      )}
      {!isOffline && !isSyncing && pendingCount > 0 && (
        <div className="flex items-center justify-between gap-3">
          <span>{pendingCount} {plural(pendingCount)} to sync</span>
          <Button type="button" size="sm" variant="outline" onClick={() => void syncNow()}>
            Sync now
          </Button>
        </div>
      )}
      {failedCount > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-amber-800">{failedCount} {plural(failedCount)} failed to sync</span>
            <Button type="button" size="sm" variant="outline" onClick={() => void retryFailed()}>
              Retry
            </Button>
          </div>
          {failedItems.map((item) => (
            item.error && (
              <div key={item.id} className="text-amber-800">
                {item.error}
              </div>
            )
          ))}
        </div>
      )}
      {conflictCount > 0 && (
        <div className="space-y-1">
          <div className="text-amber-800">
            {conflictCount} {plural(conflictCount)} couldn&apos;t be recorded
          </div>
          {conflictItems.map((item) =>
            (
              <div key={item.id} className="text-amber-800">
                {item.error || conflictFallback}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
